import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import Button from "~/ui-apps/design-system/atoms/Button";

interface EvaluationChecklistIdeasOffcanvasProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    onTemplateSelect: (filters: Filter[]) => void;
    onFilterSelect: (filter: Filter) => void;
    onQuerySubmit: (data: any) => void;
    isAuthenticated: boolean;
}

interface Filter {
    glossary_key?: string;
    name: string;
    key: string;
    query: string;
    short_description: string;
    filter_type?: string;
    /** Human-readable clause for checklist UI; canonical `query` stays API-shaped */
    displayLabel?: string;
}

interface ChecklistIdea {
    title: string;
    lgOnly?: boolean;
    filters: Filter[];
    shortDescription?: string;
    filter_type?: string;
}

export const EvaluationChecklistIdeasOffcanvas: React.FC<EvaluationChecklistIdeasOffcanvasProps> = ({ isOpen, onClose, onTemplateSelect, onFilterSelect, onQuerySubmit, isAuthenticated }) => {
    const [isFiltersOffcanvasOpen, setIsFiltersOffcanvasOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

    const checklistIdeas: ChecklistIdea[] = [
        {
            title: "Growth Stocks",
            shortDescription: "Showing revenue growth and strong profitability.",
            filters: [
                {
                    name: "Revenue Growth (3Y CAGR) > 20%",
                    key: "revenue_growth_past_3_years_cagr",
                    glossary_key: "revenue_ttm",
                    query: "revenue_growth_past_3_years_cagr > 20%",
                    short_description: "Compound annual growth rate of revenue over 3 years.",
                    filter_type: "Percentage"
                },
                {
                    name: "Gross Profit Margin (TTM) > 40%",
                    key: "gross_profit_margin_ttm",
                    glossary_key: "gross_profit_margin_ttm",
                    query: "gross_profit_margin_ttm > 40%",
                    short_description: "Percentage of revenue remaining after subtracting the cost of goods sold",
                    filter_type: "Percentage"
                },
                {
                    name: "Free Cash Flow (TTM) > 0",
                    key: "free_cash_flow_ttm",
                    glossary_key: "free_cash_flow_ttm",
                    query: "free_cash_flow_ttm > 0",
                    short_description: "Free Cash Flow in last twelve months",
                    filter_type: "big_number"
                }
            ]
        },
        {
            title: "Value Stocks",
            shortDescription: "These stocks are often undervalued and have strong fundamentals.",
            filters: [
                {
                    name: "P/E < 15",
                    key: "price_to_earnings_ratio",
                    glossary_key: "price_to_earnings_ratio",
                    query: "price_to_earnings_ratio < 15",
                    short_description: "Stock price divided by its earnings per share.",
                    filter_type: "small_number"
                },
                    {
                    name: "Debt/Equity < 1",
                    key: "debt_to_equity_ratio_current",
                    glossary_key: "debt_to_equity_ratio_current",
                    query: "debt_to_equity_ratio_current < 1",
                    short_description: "How much debt a company is using to finance its assets relative to the value of shareholders' equity",
                    filter_type: "small_number"
                },
                {
                    name: "Postive Free Cash Flow (3Y) = true",
                    key: "is_free_cash_flow_positive_past_3_years",
                    glossary_key: "free_cash_flow_ttm",
                    query: "is_free_cash_flow_positive_past_3_years = true",
                    short_description: "Has positive free cash flow over the past 3 years",
                    filter_type: "boolean"
                },
                {
                    name: "Has Positive EPS - past 3 years = true",
                    key: "has_positive_eps_past_3_years",
                    glossary_key: "eps_ttm",
                    query: "has_positive_eps_past_3_years = true",
                    short_description: "Earnings per share has been positive in the past 3 years.",
                    filter_type: "boolean"
                }
            ]
        },
        {
            title: "Revenue Growth Turnaround",
            shortDescription: "These stocks have experienced a revenue growth turnaround and improved profitability over the past 3 years.",
            filters: [
                {
                    name: "Revenue Turnaround (3Y) = true",
                    key: "has_revenue_growth_turned_around_past_3_years",
                    glossary_key: "revenue_ttm",
                    query: "has_revenue_growth_turned_around_past_3_years = true",
                    short_description: "Revenue growth has turned around in the past 3 years.",
                    filter_type: "boolean"
                },
                {
                    name: "Gross Margin Improved (6Q) = true",
                    key: "has_gross_profit_margin_improved_past_6_quarters",
                    glossary_key: "gross_profit_margin_ttm",
                    query: "has_gross_profit_margin_improved_past_6_quarters = true",
                    short_description: "Has gross profit margin improved quarterly over the last 6 earnings.",
                    filter_type: "boolean"
                },
                {
                    name: "Free Cash Flow (TTM) > 0",
                    key: "free_cash_flow_ttm",
                    glossary_key: "free_cash_flow_ttm",
                    query: "free_cash_flow_ttm > 0",
                    short_description: "Free cash flow has been positive in the trailing 12 months.",
                    filter_type: "small_number"
                }
            ]
        },
        {
            title: "Healthy Financials",
            shortDescription: "These stocks offer a consistent dividend yield and have a strong track record of paying dividends.",
            filters: [
                {
                    name: "Debt/Equity < 1",
                    key: "debt_to_equity_ratio_current",
                    query: "debt_to_equity_ratio_current < 1",
                    glossary_key: "debt_to_equity_ratio_current",
                    short_description: "How much debt a company is using to finance its assets relative to the value of shareholders' equity",
                    filter_type: "small_number"
                },
                {
                    name: "Free Cash Flow (TTM) > 0",
                    key: "free_cash_flow_ttm",
                    glossary_key: "free_cash_flow_ttm",
                    query: "free_cash_flow_ttm > 0",
                    short_description: "Free cash flow has been positive in the trailing 12 months.",
                    filter_type: "small_number"
                },
                {
                    name: "Has Positive EPS - past 3 years = true",
                    key: "has_positive_eps_past_3_years",
                    glossary_key: "eps_ttm",
                    query: "has_positive_eps_past_3_years = true",
                    short_description: "Earnings per share has been positive in the past 3 years.",
                    filter_type: "boolean"
                },
                {
                    name: "Gross Margin (TTM) > 40%",
                    key: "gross_profit_margin_ttm",
                    glossary_key: "gross_profit_margin_ttm",
                    query: "gross_profit_margin_ttm > 40%",
                    short_description: "Percentage of revenue remaining after subtracting the cost of goods sold",
                    filter_type: "Percentage"
                }
            ]
        },
        {
            title: "Risky with asymmetric upside",
            shortDescription: "Stocks with high risk of failure but with high potential for upside.",
            filters: [
                {
                    name: "Net Income (TTM) < 0",
                    key: "net_income_ttm",
                    glossary_key: "net_income_ttm",
                    query: "net_income_ttm < 0",
                    short_description: "Net Income in last 12 months.",
                    filter_type: "small_number"
                },
                {
                    name: "Revenue Growth (3Y CAGR) > 25%",
                    key: "revenue_growth_past_3_years_cagr",
                    glossary_key: "revenue_ttm",
                    query: "revenue_growth_past_3_years_cagr > 25%",
                    short_description: "Compound annual growth rate of revenue over 3 years.",
                    filter_type: "Percentage"
                },
                {
                    name: "Revenue Improved (6Q) = true",
                    key: "has_revenue_improved_past_6_quarters",
                    glossary_key: "revenue_ttm",
                    query: "has_revenue_improved_past_6_quarters = true",
                    short_description: "Revenue has improved over the past 6 quarters.",
                    filter_type: "boolean"
                },
                {
                    name: "Gross Margin (TTM) > 40%",
                    key: "gross_profit_margin_ttm",
                    glossary_key: "gross_profit_margin_ttm",
                    query: "gross_profit_margin_ttm > 40%",
                    short_description: "Percentage of revenue remaining after subtracting the cost of goods sold",
                    filter_type: "Percentage"
                },
            ]
        }
    ];

    return (
        <>
            <Transition show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <TransitionChild
                        enter="ease-in-out duration-500"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-500"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div className="fixed inset-0 overflow-hidden">
                        <TransitionChild
                            enter="transform transition ease-in-out duration-500"
                            enterFrom="translate-x-full"
                            enterTo="translate-x-0"
                            leave="transform transition ease-in-out duration-500"
                            leaveFrom="translate-x-0"
                            leaveTo="translate-x-full"
                        >
                            <DialogPanel className="pointer-events-auto absolute right-0 h-full w-screen max-w-full md:max-w-xl bg-white shadow-xl flex flex-col">
                                <div className="sticky top-0 z-10 px-4 py-5 sm:px-6 flex-shrink-0">
                                    <button
                                        type="button"
                                        className="float-right text-gray-900 hover:text-gray-700"
                                        onClick={() => onClose(false)}
                                    >
                                        <FontAwesomeIcon icon={faCircleXmark} className="text-2xl text-red-500" aria-hidden="true" />
                                        <span className="sr-only">Close panel</span>
                                    </button>
                                    <h2 className="text-lg font-semibold text-black">Evaluation Templates</h2>
                                    <hr className="mt-2" />
                                </div>

                                <div className="flex-1 overflow-y-auto px-1.5 pb-6 sm:px-12">
                                    {checklistIdeas.map((idea, ideaIndex) => (
                                        <div
                                            key={ideaIndex}
                                            className="group relative mb-6 min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-background-dark shadow-md shadow-slate-900/[0.04] transition-all duration-300 last:mb-0 hover:border-emerald-200/70 hover:shadow-lg hover:shadow-emerald-900/[0.05]"
                                        >
                                            <div
                                                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                                aria-hidden
                                            />
                                            <div className="relative min-w-0 py-3 pl-2 pr-2 md:py-4 md:pl-5 md:pr-5">
                                                <div className="flex min-w-0 items-start gap-1.5 md:gap-3">
                                                    <FontAwesomeIcon
                                                        icon={faLightbulb}
                                                        className="mt-0.5 h-[1.125rem] w-[1.125rem] shrink-0 text-amber-500 md:mt-1.5 md:h-5 md:w-5"
                                                        aria-hidden
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex min-w-0 items-start gap-2 md:gap-3">
                                                            <h3 className="min-w-0 flex-1 break-words text-base font-bold leading-snug tracking-tight text-slate-900 md:text-lg">
                                                                {idea.title}
                                                            </h3>
                                                            <Button
                                                                type="primary"
                                                                size="xs"
                                                                className="mt-0.5 shrink-0 self-start md:mt-1"
                                                                onClick={() => onTemplateSelect(idea.filters)}
                                                            >
                                                                Add Template
                                                            </Button>
                                                        </div>
                                                        {idea.shortDescription ? (
                                                            <p className="mt-1.5 text-[11px] font-normal leading-snug text-yellow-800 md:mt-2 md:text-xs">
                                                                {idea.shortDescription}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="border-t border-slate-200/80 px-2 pb-3 pt-0 md:px-5 md:pb-4">
                                                <div
                                                    className="mt-3 grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-1.5 md:grid-cols-[1.625rem_minmax(0,1fr)] md:gap-x-2.5"
                                                    aria-label={`${idea.title} criteria sequence`}
                                                >
                                                    {idea.filters.map((filter, index) => {
                                                        const n = idea.filters.length;
                                                        const hasMoreBelow = index < n - 1;
                                                        const isLastOnly = index === n - 1;
                                                        const showTimeline = n > 1;
                                                        // Pills align with filter tags (top + mt-0.5); line anchors to pill vertical center (h-6 → 0.75rem radius).
                                                        const pillCenterTop = 'top-[calc(0.125rem+0.75rem)]';
                                                        let linePosition = '';
                                                        if (showTimeline) {
                                                            if (index === 0 && hasMoreBelow) {
                                                                linePosition = `${pillCenterTop} bottom-0`;
                                                            } else if (isLastOnly) {
                                                                linePosition =
                                                                    'top-0 bottom-auto h-[calc(0.125rem+0.75rem)]';
                                                            } else {
                                                                linePosition = 'top-0 bottom-0';
                                                            }
                                                        }

                                                        return (
                                                            <Fragment key={index}>
                                                                <div
                                                                    className={`relative grid min-h-0 justify-items-start ${
                                                                        hasMoreBelow
                                                                            ? 'grid-rows-[minmax(0,1fr)_1rem] md:grid-rows-[minmax(0,1fr)_1rem]'
                                                                            : 'grid-rows-[minmax(0,1fr)]'
                                                                    }`}
                                                                >
                                                                    {showTimeline ? (
                                                                        <div
                                                                            className={`pointer-events-none absolute left-[0.75rem] z-0 w-[2px] -translate-x-1/2 bg-gradient-to-b from-emerald-400/70 via-slate-200 to-slate-200 md:left-[0.8125rem] ${linePosition}`}
                                                                            aria-hidden
                                                                        />
                                                                    ) : null}
                                                                    <div className="relative z-[1] row-start-1 flex h-full min-h-0 w-full flex-col items-start justify-start">
                                                                        <span
                                                                            className="relative z-[1] mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-900 to-slate-700 text-[11px] font-bold tabular-nums text-white shadow-sm ring-2 ring-white"
                                                                            aria-label={`Step ${index + 1}`}
                                                                        >
                                                                            {index + 1}
                                                                        </span>
                                                                    </div>
                                                                    {hasMoreBelow ? (
                                                                        <div aria-hidden className="row-start-2 min-h-0" />
                                                                    ) : null}
                                                                </div>
                                                                <div className={hasMoreBelow ? 'pb-4 md:pb-4' : ''}>
                                                                    <div className="min-w-0">
                                                                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                                            <span className="inline-flex min-w-0 max-w-full break-words rounded-md bg-gradient-to-r from-emerald-700 to-teal-700 px-1.5 py-0.5 text-[11px] md:text-xs font-medium text-white shadow-sm md:rounded-md">
                                                                                {filter.name}
                                                                            </span>
                                                                        </div>
                                                                        <p className="mt-2 text-[11px] font-normal leading-snug text-slate-500 md:text-xs">
                                                                            {filter.short_description}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>
            
        </>
    );
};